/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import NotificationPreferencesDialog from '../shared/NotificationPreferencesDialog';
import { getNotificationRoute } from '../../utils/notificationRouting';
import MyProjectsPanel from '../shared/MyProjectsPanel';
import FacilityChatsPanel from '../shared/FacilityChatsPanel';
import '../../styles/Home.css';

const EVENT_ICONS = {
  application_submitted: '\uD83D\uDCCB',
  application_approved: '\u2705',
  application_rejected: '\u274C',
  revision_requested: '\uD83D\uDD04',
  application_revised: '\uD83D\uDCDD',
  chamber_assigned: '\uD83C\uDFE0',
  chamber_updated: '\uD83D\uDD27',
  chamber_completed: '\u2705',
  chamber_deleted: '\uD83D\uDDD1\uFE0F',
  resource_assigned: '\uD83D\uDCE6',
  resource_updated: '\uD83D\uDD27',
  invoice_created: '\uD83D\uDCCB',
  invoice_status_changed: '\uD83D\uDCCA',
  invoice_edited: '\u270F\uFE0F',
  invoice_deleted: '\uD83D\uDDD1\uFE0F',
  payment_recorded: '\uD83D\uDCB0',
  request_submitted: '\uD83D\uDCE9',
  request_completed: '\u2705',
  request_approved: '\u2705',
  request_status_changed: '\uD83D\uDD04',
  request_client_comment: '\uD83D\uDCAC',
  request_rejected: '\u274C',
  general_comment: '\uD83D\uDCAC',
  team_member_added: '\uD83D\uDC65',
  team_member_removed: '\uD83D\uDC64',
  team_role_changed: '\uD83D\uDD11',
  invitation_accepted: '\uD83E\uDD1D',
  invitation_declined: '\uD83D\uDC4B',
  project_renewed: '\uD83D\uDD04',
  project_renewal_confirmation: '\u23F0',
  project_started: '\uD83D\uDE80',
  project_completed: '\uD83C\uDFC1',
  project_terminated: '\u26D4',
};

const USER_EVENT_TYPES = [
  'general_comment', 'chamber_assigned', 'chamber_updated', 'chamber_completed', 'chamber_deleted',
  'resource_assigned', 'resource_updated', 'invoice_created', 'invoice_edited', 'invoice_deleted',
  'invoice_status_changed', 'payment_recorded', 'application_approved', 'application_rejected',
  'revision_requested', 'project_started', 'project_completed', 'project_terminated',
  'team_member_added', 'team_member_removed', 'team_role_changed',
  'request_completed', 'request_approved', 'request_rejected',
  'project_renewed', 'project_renewal_confirmation',
];

const ADMIN_EVENT_TYPES = [
  'application_submitted', 'application_revised', 'request_submitted',
  'invitation_accepted', 'invitation_declined',
];

const NOTIF_PAGE_SIZE = 30;

const NOTIF_FILTER_CATEGORIES = {
  all: { label: 'All', types: null },
  applications: {
    label: 'New Applications',
    types: ['application_submitted', 'application_approved', 'application_rejected', 'revision_requested', 'application_revised']
  },
  project: {
    label: 'Ongoing Projects',
    types: ['project_started', 'project_completed', 'project_terminated', 'project_renewed', 'project_renewal_confirmation', 'general_comment']
  },
  chambers: {
    label: 'Compartment Assignment',
    types: ['chamber_assigned', 'chamber_updated', 'chamber_completed', 'chamber_deleted']
  },
  resources: {
    label: 'Resource Assignment',
    types: ['resource_assigned', 'resource_updated']
  },
  invoices: {
    label: 'Invoices',
    types: ['invoice_created', 'invoice_edited', 'invoice_deleted', 'invoice_status_changed', 'payment_recorded']
  },
  requests: {
    label: 'Requests',
    types: [
      'request_submitted', 'request_completed', 'request_rejected',
      'request_status_changed', 'request_client_comment',
      'request_approved'
    ]
  },
  team: {
    label: 'Project Team',
    types: ['team_member_added', 'team_member_removed', 'team_role_changed', 'invitation_accepted', 'invitation_declined']
  },
};

const formatTimeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { refreshNotifications } = useNotifications();
  const isAdmin = user?.institution_roles?.some(r => r.role === 'Admin');

  const [projects, setProjects] = useState([]);
  const [pendingProjects, setPendingProjects] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPrefs, setShowPrefs] = useState(false);

  // Pagination state
  const [userNotifPage, setUserNotifPage] = useState(1);
  const [adminNotifPage, setAdminNotifPage] = useState(1);
  const [userHasMore, setUserHasMore] = useState(false);
  const [adminHasMore, setAdminHasMore] = useState(false);
  const [loadingMoreUser, setLoadingMoreUser] = useState(false);
  const [loadingMoreAdmin, setLoadingMoreAdmin] = useState(false);

  // Section collapse state — persisted in localStorage
  const SECTION_DEFAULTS = {
    userNotif: true,
    projects: true,
    invitations: true,
    adminNotif: true,
    adminChats: true,
  };
  const [sections, setSections] = useState(() => {
    try {
      const saved = localStorage.getItem('homeSections');
      if (saved) return { ...SECTION_DEFAULTS, ...JSON.parse(saved) };
    } catch {}
    return SECTION_DEFAULTS;
  });

  // Notification filter tabs
  const [userNotifTab, setUserNotifTab] = useState('all');
  const [adminNotifTab, setAdminNotifTab] = useState('all');

  // Category filters
  const [userCategoryFilter, setUserCategoryFilter] = useState('all');
  const [adminCategoryFilter, setAdminCategoryFilter] = useState('all');

  // Selection state for bulk actions
  const [selectedUserNotifs, setSelectedUserNotifs] = useState(new Set());
  const [selectedAdminNotifs, setSelectedAdminNotifs] = useState(new Set());

  // Unread badges for the chat panels. These callbacks must keep a stable
  // identity, otherwise the panels re-run their reporting effect every render.
  const [teamChatUnread, setTeamChatUnread] = useState(0);
  const [projectNotifUnread, setProjectNotifUnread] = useState(0);
  const [facilityChatUnread, setFacilityChatUnread] = useState(0);
  const handleTeamChatUnread = useCallback((value) => {
    if (value && typeof value === 'object') {
      setTeamChatUnread(value.chat || 0);
      setProjectNotifUnread(value.notifications || 0);
    } else {
      setTeamChatUnread(value || 0);
    }
  }, []);
  const handleFacilityChatUnread = useCallback((count) => setFacilityChatUnread(count), []);

  // Draggable resizer state
  const [splitPercent, setSplitPercent] = useState(55);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = Math.min(Math.max((x / rect.width) * 100, 25), 75);
      setSplitPercent(pct);
    };

    const handleMouseUp = () => {
      if (dragging.current) {
        dragging.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const toggleSection = (key) => {
    setSections(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('homeSections', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const fetchUserNotifications = useCallback(async (filterKey) => {
    try {
      const params = { page: 1, limit: 200, scope: 'user' };
      const catTypes = NOTIF_FILTER_CATEGORIES[filterKey]?.types;
      if (catTypes) params.event_types = catTypes.join(',');
      const res = await axios.get('/api/notifications', { params });
      setNotifications(res.data?.notifications || []);
      setUserHasMore(res.data?.hasMore || false);
      setUserNotifPage(1);
      setSelectedUserNotifs(new Set());
    } catch (err) {
      console.error('User notifications fetch error:', err);
    }
  }, []);

  const fetchAdminNotifications = useCallback(async (filterKey) => {
    try {
      const params = { page: 1, limit: NOTIF_PAGE_SIZE };
      const catTypes = NOTIF_FILTER_CATEGORIES[filterKey]?.types;
      if (catTypes) params.event_types = catTypes.join(',');
      const res = await axios.get('/api/notifications', { params });
      setAdminNotifications(res.data?.notifications || []);
      setAdminHasMore(res.data?.hasMore || false);
      setAdminNotifPage(1);
      setSelectedAdminNotifs(new Set());
    } catch (err) {
      console.error('Admin notifications fetch error:', err);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [projectsRes, pendingRes] = await Promise.all([
        axios.get('/api/applications/saved').catch(e => { console.error('Projects fetch error:', e); return { data: [] }; }),
        axios.get('/api/applications/pending-for-user').catch(e => { console.error('Pending fetch error:', e); return { data: [] }; }),
      ]);

      setProjects(projectsRes.data || []);
      setPendingProjects(pendingRes.data || []);

      await fetchUserNotifications(userCategoryFilter);

      if (isAdmin) {
        await fetchAdminNotifications(adminCategoryFilter);
      }
    } catch (err) {
      console.error('Error loading home data:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, userCategoryFilter, adminCategoryFilter, fetchUserNotifications, fetchAdminNotifications]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Re-fetch only user notifications when the user category filter changes
  const userCategoryFilterRef = useRef(userCategoryFilter);
  useEffect(() => {
    if (userCategoryFilterRef.current !== userCategoryFilter) {
      userCategoryFilterRef.current = userCategoryFilter;
      fetchUserNotifications(userCategoryFilter);
    }
  }, [userCategoryFilter, fetchUserNotifications]);

  // Re-fetch only admin notifications when the admin category filter changes
  const adminCategoryFilterRef = useRef(adminCategoryFilter);
  useEffect(() => {
    if (adminCategoryFilterRef.current !== adminCategoryFilter) {
      adminCategoryFilterRef.current = adminCategoryFilter;
      fetchAdminNotifications(adminCategoryFilter);
    }
  }, [adminCategoryFilter, fetchAdminNotifications]);

  const handleNotifClick = async (notif, isAdminNotif = false) => {
    if (!notif.is_read) {
      try {
        await axios.patch(`/api/notifications/${notif._id}/read`);
        refreshNotifications();
        fetchData();
      } catch (err) {
        console.error('Failed to mark as read:', err);
      }
    }

    const route = getNotificationRoute(notif, isAdminNotif);
    if (route) navigate(route);
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.patch('/api/notifications/read-all');
      refreshNotifications();
      fetchData();
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleAcceptInvitation = async (projectId) => {
    try {
      await axios.post(`/api/applications/accept-invitation/${projectId}`);
      fetchData();
    } catch (err) {
      console.error('Error accepting invitation:', err);
    }
  };

  const handleDeclineInvitation = async (projectId) => {
    try {
      await axios.post(`/api/applications/decline-invitation/${projectId}`);
      fetchData();
    } catch (err) {
      console.error('Error declining invitation:', err);
    }
  };

  const filterNotifs = (notifs, tab) => {
    if (tab === 'unread') return notifs.filter(n => !n.is_read);
    if (tab === 'read') return notifs.filter(n => n.is_read);
    return notifs;
  };

  const loadMoreUserNotifs = useCallback(async () => {
    setLoadingMoreUser(true);
    try {
      const nextPage = userNotifPage + 1;
      const params = { page: nextPage, limit: NOTIF_PAGE_SIZE, scope: 'user' };
      const catTypes = NOTIF_FILTER_CATEGORIES[userCategoryFilter]?.types;
      if (catTypes) params.event_types = catTypes.join(',');
      const res = await axios.get('/api/notifications', { params });
      const moreNotifs = res.data?.notifications || [];
      setNotifications(prev => [...prev, ...moreNotifs]);
      setUserHasMore(res.data?.hasMore || false);
      setUserNotifPage(nextPage);
    } catch (err) {
      console.error('Error loading more user notifications:', err);
    } finally {
      setLoadingMoreUser(false);
    }
  }, [userNotifPage, userCategoryFilter]);

  const loadMoreAdminNotifs = useCallback(async () => {
    setLoadingMoreAdmin(true);
    try {
      const nextPage = adminNotifPage + 1;
      const params = { page: nextPage, limit: NOTIF_PAGE_SIZE };
      const catTypes = NOTIF_FILTER_CATEGORIES[adminCategoryFilter]?.types;
      if (catTypes) params.event_types = catTypes.join(',');
      const res = await axios.get('/api/notifications', { params });
      const moreNotifs = res.data?.notifications || [];
      setAdminNotifications(prev => [...prev, ...moreNotifs]);
      setAdminHasMore(res.data?.hasMore || false);
      setAdminNotifPage(nextPage);
    } catch (err) {
      console.error('Error loading more admin notifications:', err);
    } finally {
      setLoadingMoreAdmin(false);
    }
  }, [adminNotifPage, adminCategoryFilter]);

  const handleBulkAction = async (action, ids, isAdminNotif) => {
    if (ids.length === 0) return;
    try {
      if (action === 'read') {
        await axios.patch('/api/notifications/bulk-read', { ids });
      } else if (action === 'unread') {
        await axios.patch('/api/notifications/bulk-unread', { ids });
      } else if (action === 'delete') {
        await axios.patch('/api/notifications/bulk-delete', { ids });
      }
      if (isAdminNotif) {
        setSelectedAdminNotifs(new Set());
      } else {
        setSelectedUserNotifs(new Set());
      }
      refreshNotifications();
      fetchData();
    } catch (err) {
      console.error(`Failed to ${action} notifications:`, err);
    }
  };

  const toggleNotifSelection = (id, isAdminNotif) => {
    const setter = isAdminNotif ? setSelectedAdminNotifs : setSelectedUserNotifs;
    setter(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (notifs, tab, isAdminNotif) => {
    const filtered = filterNotifs(notifs, tab);
    const setter = isAdminNotif ? setSelectedAdminNotifs : setSelectedUserNotifs;
    const selected = isAdminNotif ? selectedAdminNotifs : selectedUserNotifs;
    const allSelected = filtered.length > 0 && filtered.every(n => selected.has(n._id));
    if (allSelected) {
      setter(new Set());
    } else {
      setter(new Set(filtered.map(n => n._id)));
    }
  };

  const unreadUserCount = notifications.filter(n => !n.is_read).length;
  const unreadAdminCount = adminNotifications.filter(n => !n.is_read).length;

  if (loading) {
    return <div className="home-page"><div className="home-loading">Loading your dashboard...</div></div>;
  }

  const renderNotifList = (notifs, tab, isAdminNotif, { hasMore, loading: loadingMore, onLoadMore } = {}) => {
    const filtered = filterNotifs(notifs, tab);
    const selected = isAdminNotif ? selectedAdminNotifs : selectedUserNotifs;
    const allSelected = filtered.length > 0 && filtered.every(n => selected.has(n._id));
    const someSelected = selected.size > 0;

    if (filtered.length === 0) {
      return (
        <div className="home-notif-empty">
          {tab === 'unread' ? 'No unread notifications' : tab === 'read' ? 'No read notifications' : 'No notifications yet'}
        </div>
      );
    }

    return (
      <>
        <div className="home-notif-toolbar">
          <label className="home-notif-select-all" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() => toggleSelectAll(notifs, tab, isAdminNotif)}
            />
            <span className="home-notif-select-all-label">
              {someSelected ? `${selected.size} selected` : 'Select all'}
            </span>
          </label>
          {someSelected && (
            <div className="home-notif-bulk-actions">
              <button
                className="home-bulk-btn"
                onClick={() => handleBulkAction('read', [...selected], isAdminNotif)}
                title="Mark selected as read"
              >
                Mark Read
              </button>
              <button
                className="home-bulk-btn"
                onClick={() => handleBulkAction('unread', [...selected], isAdminNotif)}
                title="Mark selected as unread"
              >
                Mark Unread
              </button>
              <button
                className="home-bulk-btn delete"
                onClick={() => handleBulkAction('delete', [...selected], isAdminNotif)}
                title="Delete selected notifications"
              >
                Delete
              </button>
            </div>
          )}
        </div>
        <ul className="home-notif-list">
          {filtered.map(n => (
            <li
              key={n._id}
              className={`home-notif-item ${n.is_read ? '' : 'unread'} ${selected.has(n._id) ? 'selected' : ''}`}
            >
              <label className="home-notif-checkbox" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selected.has(n._id)}
                  onChange={() => toggleNotifSelection(n._id, isAdminNotif)}
                />
              </label>
              <div className="home-notif-clickable" onClick={() => handleNotifClick(n, isAdminNotif)}>
                <span className="home-notif-icon">
                  {EVENT_ICONS[n.event_type] || '\uD83D\uDD14'}
                </span>
                <div className="home-notif-content">
                  <div className="home-notif-title">{n.title}</div>
                  {n.preview && <div className="home-notif-preview">{n.preview}</div>}
                  <div className="home-notif-meta">
                    <span className="home-notif-time">{formatTimeAgo(n.createdAt)}</span>
                    {n.project?.project_id && (
                      <span className="home-notif-project">{n.project.project_id}</span>
                    )}
                  </div>
                </div>
                {!n.is_read && <span className="home-notif-dot" />}
              </div>
            </li>
          ))}
        </ul>
        {hasMore && tab === 'all' && (
          <div className="home-notif-load-more">
            <button
              className="home-notif-load-more-btn"
              onClick={onLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading...' : 'Load more notifications'}
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="home-page">
      <div className="home-header">
        <div className="home-header-row">
          <h1 className="home-welcome">
            Welcome back, {user?.first_name || 'User'}
          </h1>
          <p className="home-subtitle">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <button className="home-prefs-btn" onClick={() => setShowPrefs(true)}>
            Notification Preferences
          </button>
        </div>
      </div>

      <div
        className={`home-columns ${isAdmin ? '' : 'single-column'}`}
        ref={containerRef}
      >
        {/* ===== LEFT COLUMN — User Dashboard ===== */}
        <div
          className="home-col-left"
          style={isAdmin ? { flex: `0 0 calc(${splitPercent}% - 12px)`, maxWidth: `calc(${splitPercent}% - 12px)` } : undefined}
        >
          <div className="home-pane-heading">User Dashboard</div>

          {/* Project Invitations */}
          {pendingProjects.length > 0 && (
            <div className="home-section">
              <div className="home-section-header" onClick={() => toggleSection('invitations')}>
                <div className="home-section-header-left">
                  <span className="home-section-icon">{'\uD83D\uDCE8'}</span>
                  <span className="home-section-title">Project Invitations</span>
                  <span className="home-section-badge unread">{pendingProjects.length}</span>
                </div>
                <span className={`home-section-chevron ${sections.invitations ? 'open' : ''}`}>&#9662;</span>
              </div>
              <div className={`home-section-body ${sections.invitations ? '' : 'collapsed'}`}>
                {pendingProjects.map(proj => (
                  <div key={proj._id} className="home-invitation-card">
                    <div className="home-invitation-info">
                      <div className="home-invitation-title">{proj.project_title}</div>
                      <div className="home-invitation-detail">
                        PI: {proj.pi_name} &middot; {proj.facility?.facility_name || 'Unknown Facility'} &middot; Role: {proj.role_in_project || 'Team Member'}
                      </div>
                    </div>
                    <div className="home-invitation-actions">
                      <button className="home-invitation-btn accept" onClick={() => handleAcceptInvitation(proj._id)}>Accept</button>
                      <button className="home-invitation-btn decline" onClick={() => handleDeclineInvitation(proj._id)}>Decline</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* My Projects — active first, then completed; notifications expand per row */}
          <div className="home-section">
            <div className="home-section-header" onClick={() => toggleSection('projects')}>
              <div className="home-section-header-left">
                <span className="home-section-icon">{'\uD83D\uDCC2'}</span>
                <span className="home-section-title">My Projects</span>
                <span className="home-section-badge">{projects.length}</span>
                {projectNotifUnread > 0 && (
                  <span className="home-section-badge unread">{projectNotifUnread} new</span>
                )}
                {teamChatUnread > 0 && (
                  <span className="home-section-badge unread">{teamChatUnread} chat</span>
                )}
              </div>
              <span className={`home-section-chevron ${sections.projects ? 'open' : ''}`}>&#9662;</span>
            </div>
            <div className={`home-section-body home-section-body-tall ${sections.projects ? '' : 'collapsed'}`}>
              <MyProjectsPanel
                projects={projects}
                user={user}
                notifications={notifications}
                onUnreadTotalChange={handleTeamChatUnread}
                onNotificationClick={(n) => handleNotifClick(n, false)}
                onNotificationsChanged={fetchData}
              />
            </div>
          </div>
        </div>

        {/* ===== DRAGGABLE DIVIDER ===== */}
        {isAdmin && (
          <div
            className="home-resizer"
            onMouseDown={handleMouseDown}
            title="Drag to resize"
          >
            <div className="home-resizer-line" />
          </div>
        )}

        {/* ===== RIGHT COLUMN — Admin Dashboard (only for admins) ===== */}
        {isAdmin && (
          <div
            className="home-col-right"
            style={{ flex: `0 0 calc(${100 - splitPercent}% - 12px)`, maxWidth: `calc(${100 - splitPercent}% - 12px)` }}
          >
            <div className="home-pane-heading">Admin Dashboard</div>

            {/* Admin Notification Center */}
            <div className="home-section">
              <div className="home-section-header" onClick={() => toggleSection('adminNotif')}>
                <div className="home-section-header-left">
                  <span className="home-section-icon">{'\uD83D\uDEE1\uFE0F'}</span>
                  <span className="home-section-title">Admin Notifications</span>
                  {unreadAdminCount > 0 && (
                    <span className="home-section-badge unread">{unreadAdminCount}</span>
                  )}
                </div>
                <div className="home-section-actions">
                  {unreadAdminCount > 0 && (
                    <button
                      className="home-section-action-btn"
                      onClick={(e) => { e.stopPropagation(); handleMarkAllRead(); }}
                    >
                      Mark all read
                    </button>
                  )}
                  <span className={`home-section-chevron ${sections.adminNotif ? 'open' : ''}`}>&#9662;</span>
                </div>
              </div>
              <div className={`home-section-body ${sections.adminNotif ? '' : 'collapsed'}`}>
                <div className="home-notif-tabs">
                  <button className={`home-notif-tab ${adminNotifTab === 'all' ? 'active' : ''}`} onClick={() => setAdminNotifTab('all')}>
                    All
                  </button>
                  <button className={`home-notif-tab ${adminNotifTab === 'unread' ? 'active' : ''}`} onClick={() => setAdminNotifTab('unread')}>
                    Unread {unreadAdminCount > 0 && `(${unreadAdminCount})`}
                  </button>
                  <button className={`home-notif-tab ${adminNotifTab === 'read' ? 'active' : ''}`} onClick={() => setAdminNotifTab('read')}>
                    Read
                  </button>
                </div>
                <div className="home-admin-category-filters">
                  <label className="home-category-filter-label">Filter:</label>
                  <select
                    className="home-category-filter-select"
                    value={adminCategoryFilter}
                    onChange={(e) => setAdminCategoryFilter(e.target.value)}
                  >
                    {Object.entries(NOTIF_FILTER_CATEGORIES).map(([key, { label }]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                {renderNotifList(adminNotifications, adminNotifTab, true, {
                  hasMore: adminHasMore,
                  loading: loadingMoreAdmin,
                  onLoadMore: loadMoreAdminNotifs
                })}
              </div>
            </div>

            {/* Admin Chats — facility chat groups, not project team chats */}
            <div className="home-section">
              <div className="home-section-header" onClick={() => toggleSection('adminChats')}>
                <div className="home-section-header-left">
                  <span className="home-section-icon">{'\uD83D\uDDE8\uFE0F'}</span>
                  <span className="home-section-title">Chats</span>
                  {facilityChatUnread > 0 && (
                    <span className="home-section-badge unread">{facilityChatUnread}</span>
                  )}
                </div>
                <div className="home-section-actions">
                  <Link
                    to="/admin/chats"
                    className="home-section-action-btn"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Manage
                  </Link>
                  <span className={`home-section-chevron ${sections.adminChats ? 'open' : ''}`}>&#9662;</span>
                </div>
              </div>
              <div className={`home-section-body ${sections.adminChats ? '' : 'collapsed'}`}>
                <FacilityChatsPanel
                  user={user}
                  onUnreadTotalChange={handleFacilityChatUnread}
                  manageLink="/admin/chats"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <NotificationPreferencesDialog
        open={showPrefs}
        onClose={() => setShowPrefs(false)}
      />
    </div>
  );
};

export default Home;
